import React, { useState, useEffect } from "react";
import UserTable from "./UserTable"; // Import the UserTable component
import axiosInstance from "../../utils/axiosInstance";
import PageHeader from "../../Components/PageHeader";

function Users() {
  const [users, setUsers] = useState([]); // State to store user data
  const [loading, setLoading] = useState(true); // Loading state to show loading indicator
  const [searchQuery, setSearchQuery] = useState(""); // State for the search query
  const [currentPage, setCurrentPage] = useState(1); // Current pagination page
  const rowsPerPage = 10; // Rows per page

  // Fetch data from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get("/users"); // No need to add full URL, `axiosInstance` handles it
        const data = response.data; // Axios automatically parses JSON

        // Log the response to check the structure
        console.log(data);

        if (data.success && Array.isArray(data.users)) {
          const enrichedData = data.users.map((user) => ({
            ...user,
            name: user.name || "--",
            place: user.place || "--",
          }));
          setUsers(enrichedData);
        } else {
          console.error("Fetched data is not in the expected format:", data);
          setUsers([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter users based on the search query
  const filteredUsers = users.filter(
    (user) =>
      (user.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (user.place?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (user.phone || "").includes(searchQuery)
  );

  // Reset to the first page when the search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Get the current page's users
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstRow, indexOfLastRow);

  // Pagination controls
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  if (loading) {
    return (
      <div className="flex h-screen justify-center items-center bg-gray-100">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <PageHeader
        title="Users"
        subtitle="Manage and view all registered users"
        showBackButton={true}
      />

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative w-full sm:w-96">
          <input
            type="text"
            placeholder="Search users by name, place, or phone..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M11 16a7 7 0 100-14 7 7 0 000 14zM21 21l-4.35-4.35"
            />
          </svg>
        </div>
      </div>

      {/* UserTable with current users */}
      <UserTable users={currentUsers} />

      {/* Pagination Controls */}
      <div className="flex justify-center items-center mt-6 space-x-4">
        <button
          className={`px-4 py-2 rounded ${
            currentPage === 1
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-gray-600">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className={`px-4 py-2 rounded ${
            currentPage === totalPages || totalPages === 0
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
          onClick={handleNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default Users;
